import { useCallback, useEffect, useRef, useState } from 'react';
import type { FinanceSyncProgressMessage, FinanceSyncStatus } from '@ozon-ai/ozon/contracts';
import { Button } from '@/components/ui/button.tsx';
import { api } from '../../../api/client';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useFinanceSyncProgressWs } from '../../../hooks/useFinanceSyncProgressWs';
import {
  SettingsErrorAlert,
  SettingsFormCard,
  SettingsHint,
  SettingsSubPageShell,
} from '../settings-shared';

function moscowYear(): number {
  return Number(
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow', year: 'numeric' }).format(new Date()),
  );
}

const YEARS = Array.from({ length: 6 }, (_, index) => moscowYear() - index);

function formatSyncedAt(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' });
}

export function SettingsOzonFinancePage() {
  const [year, setYear] = useState(YEARS[0]);
  const yearRef = useRef(year);
  yearRef.current = year;
  const [status, setStatus] = useState<FinanceSyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (selectedYear: number) => {
    setLoading(true);
    setError(null);
    try {
      const next = await api.get<FinanceSyncStatus>(`/finance/sync/status?year=${selectedYear}`);
      if (yearRef.current !== selectedYear) return;
      setStatus(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось прочитать статус');
    } finally {
      setLoading(false);
    }
  }, []);

  const onProgress = useCallback(
    (message: FinanceSyncProgressMessage) => {
      if (message.year !== year) return;
      if (message.status === 'succeeded' || message.status === 'failed') {
        if (message.status === 'failed' && message.error) setError(message.error);
        void load(year);
        return;
      }
      setStatus((current) =>
        current
          ? {
              ...current,
              processedDays: message.processedDays,
              totalDays: message.totalDays,
              operationCount: message.operationCount,
              running: true,
              status:
                message.totalDays > 0 && message.processedDays === message.totalDays
                  ? 'synced'
                  : 'not_synced',
            }
          : current,
      );
    },
    [load, year],
  );

  useFinanceSyncProgressWs(true, onProgress);

  useEffect(() => {
    void load(year);
  }, [load, year]);

  const syncNow = async () => {
    setBusy(true);
    setError(null);
    try {
      const started = await api.post<{ alreadyRunning: boolean }>('/finance/sync', { year });
      setStatus((current) => (current ? { ...current, running: true } : current));
      if (started.alreadyRunning) {
        setError('Синхронизация уже идёт');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось запустить синхронизацию');
    } finally {
      setBusy(false);
    }
  };

  const resetSync = async () => {
    const confirmed = window.confirm('Удалить все начисления и отметки дней синхронизации?');
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/finance/sync/reset', {});
      await load(year);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сбросить синхронизацию');
    } finally {
      setBusy(false);
    }
  };

  const running = Boolean(status?.running);
  const statusLabel = status?.status === 'synced' ? 'Синхронизировано' : 'Не синхронизировано';

  return (
    <SettingsSubPageShell title="Финансовые операции" backTo="/settings/integration/ozon-data">
      {error && <SettingsErrorAlert>{error}</SettingsErrorAlert>}
      <SettingsFormCard>
        <p className="mb-2 text-sm text-muted-foreground">Год</p>
        <div className="mb-4 flex flex-wrap gap-2">
          {YEARS.map((item) => (
            <Button
              key={item}
              type="button"
              size="sm"
              variant={item === year ? 'default' : 'outline'}
              onClick={() => setYear(item)}
            >
              {item}
            </Button>
          ))}
        </div>

        {loading && !status ? (
          <LoadingSpinner />
        ) : (
          <div className="flex flex-col gap-2 text-sm">
            <p className="font-semibold">{statusLabel}</p>
            <p>
              {status?.processedDays ?? 0} из {status?.totalDays ?? 0} дней
            </p>
            <p>Строк в базе: {status?.operationCount ?? 0}</p>
            <p>Последняя синхронизация: {formatSyncedAt(status?.lastSyncedAt ?? null)}</p>
            {running && (
              <SettingsHint>Синхронизация идёт, счётчик обновляется по мере сохранения дней</SettingsHint>
            )}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <Button type="button" disabled={busy || running} onClick={() => void syncNow()}>
            Синхронизировать сейчас
          </Button>
          <Button type="button" variant="outline" disabled={busy || running} onClick={() => void resetSync()}>
            Сброс синхронизации
          </Button>
        </div>
      </SettingsFormCard>
    </SettingsSubPageShell>
  );
}
